import { useMemo } from 'react'

import { shiftIsoDate, toIsoDate } from '@/lib/calendar/calendarDates'
import { cn } from '@/lib/utils'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * @param {string} anchorIso
 */
export function buildMonthDayRange(anchorIso) {
  const anchor = new Date(`${anchorIso}T12:00:00`)
  const year = anchor.getFullYear()
  const month = anchor.getMonth()
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  const from = toIsoDate(first)
  const to = shiftIsoDate(toIsoDate(last), 1)
  const daysInMonth = last.getDate()

  return { from, to, year, month, daysInMonth }
}

/**
 * @param {string} anchorIso
 */
function buildMonthCells(anchorIso) {
  const { from, year, month, daysInMonth } = buildMonthDayRange(anchorIso)
  const first = new Date(year, month, 1)
  const mondayOffset = (first.getDay() + 6) % 7
  /** @type {Array<{ date: string | null, inMonth: boolean }>} */
  const cells = []

  for (let i = 0; i < mondayOffset; i += 1) {
    cells.push({ date: null, inMonth: false })
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ date: shiftIsoDate(from, day - 1), inMonth: true })
  }

  while (cells.length % 7 !== 0) {
    cells.push({ date: null, inMonth: false })
  }

  return cells
}

/**
 * @param {{
 *   anchorDate: string
 *   appointments: import('@/types/api').Appointment[]
 *   today: string
 *   onSelectDay: (dayDate: string) => void
 * }} props
 */
export function MonthCalendar({ anchorDate, appointments, today, onSelectDay }) {
  const cells = useMemo(() => buildMonthCells(anchorDate), [anchorDate])

  const counts = useMemo(() => {
    /** @type {Map<string, number>} */
    const map = new Map()
    for (const appointment of appointments) {
      const startsAt = appointment.starts_at
      if (!startsAt) continue
      const day = String(startsAt).slice(0, 10)
      map.set(day, (map.get(day) ?? 0) + 1)
    }
    return map
  }, [appointments])

  return (
    <div className="overflow-hidden rounded-2xl border border-border/70 bg-card">
      <div className="grid grid-cols-7 border-b border-border/60 bg-muted/30 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-3">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, index) => {
          if (!cell.date) {
            return <div key={`empty-${index}`} className="min-h-24 border-b border-r border-border/40 bg-muted/10" />
          }

          const count = counts.get(cell.date) ?? 0
          const isToday = cell.date === today

          return (
            <button
              key={cell.date}
              type="button"
              onClick={() => onSelectDay(cell.date)}
              className={cn(
                'min-h-24 border-b border-r border-border/40 p-2 text-left transition-colors hover:bg-primary/5',
                isToday && 'bg-primary/10',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold',
                  isToday ? 'bg-primary text-primary-foreground' : 'text-foreground',
                )}
              >
                {Number(cell.date.slice(8, 10))}
              </span>
              <p className="mt-2 text-xs text-muted-foreground">
                {count === 0 ? '—' : `${count} appt${count === 1 ? '' : 's'}`}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
